// SHEET 27 → native order-sheet rows. Parses the workbook's "SHEET" tab (SheetJS) into one row per
// data line: SAP (column A), MODEL (B) and NAME (C) identify the boat, and every known spec column
// (D…DJ) is kept keyed by its letter. The raw SAP/name are returned as-is; matching them to a real
// vessel and deciding a storage key is done here too (resolveImportRows / orderSheetRoster) so the
// grid and procurement views agree and it all stays unit-testable. `XLSX` is injected for tests.

/* eslint-disable @typescript-eslint/no-explicit-any */

import { ORDER_SHEET_COLUMNS, OS_MODEL_KEY, OS_NAME_KEY, type OrderSheetRow } from '@technohull/shared';

/** spreadsheet column letter → 1-based index (A=1, Z=26, AA=27, …). */
export function colToIndex(letter: string): number {
  let n = 0;
  for (const ch of letter.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** the SAP's digit core — strip any prefix letters and leading zeros, so "J9100800" ≡ "9100800". */
export function sapCore(sap: string): string {
  return String(sap ?? '').replace(/\D/g, '').replace(/^0+/, '');
}

/** normalise a vessel name for tolerant matching (trim, upper-case, collapse inner whitespace). */
export function normName(name: string): string {
  return String(name ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
}

/** one parsed workbook line: raw identity (any may be blank) plus its spec cells. */
export interface ParsedOrderSheetRow {
  sap: string;
  model: string;
  name: string;
  data: Record<string, string>;
}

/** parse the order sheet into rows. Column A = SAP, B = MODEL, C = NAME; empty spec cells omitted. A
 *  line is kept when it identifies a boat (SAP or name) or carries any spec data. */
export function parseOrderSheetWorkbook(XLSX: any, buf: ArrayBuffer): ParsedOrderSheetRow[] {
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets['SHEET'] ?? wb.Sheets[(wb.SheetNames ?? [])[0]];
  if (!ws) return [];
  const aoa: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false, raw: false });
  const rows: ParsedOrderSheetRow[] = [];
  for (let r = 1; r < aoa.length; r++) {
    const line = aoa[r] ?? [];
    const sap = String(line[0] ?? '').trim();
    const model = String(line[1] ?? '').trim();
    const name = String(line[2] ?? '').trim();
    const data: Record<string, string> = {};
    for (const col of ORDER_SHEET_COLUMNS) {
      const v = line[colToIndex(col.key) - 1];
      const s = v == null ? '' : String(v).trim();
      if (s) data[col.key] = s;
    }
    if (!sap && !name && Object.keys(data).length === 0) continue; // truly blank line
    rows.push({ sap, model, name, data });
  }
  return rows;
}

/** a boat we can match an imported row against (the season overview vessels). */
export interface MatchVessel {
  sap: string;
  name: string;
}

/** the storage key for an imported boat that isn't on the overview: its SAP, or a stable name key. */
export function orphanKey(sap: string, name: string): string {
  const s = String(sap ?? '').trim();
  if (s) return s.slice(0, 80);
  const n = normName(name);
  return n ? `name:${n}`.slice(0, 80) : '';
}

export interface ImportResolution {
  /** ready to POST to /api/order-sheet/import */
  rows: OrderSheetRow[];
  /** rows matched to an overview vessel (by SAP, then by name) */
  matched: number;
  /** rows kept for boats that aren't on the overview (no/unknown SAP included) */
  orphans: number;
  /** lines that identify no boat at all and were skipped */
  skipped: number;
}

/** turn parsed workbook rows into import payload rows. Every identifiable boat is kept — matched ones
 *  key to their overview SAP; the rest (boats not on the overview, with or without a SAP) key to a
 *  synthetic id and carry their name/model so the grid can still show them. Nothing is silently
 *  dropped except a line that names no boat and has no data. */
export function resolveImportRows(parsed: ParsedOrderSheetRow[], vessels: MatchVessel[]): ImportResolution {
  const bySap = new Map<string, MatchVessel>();
  const byName = new Map<string, MatchVessel>();
  for (const v of vessels) {
    if (v.sap) bySap.set(sapCore(v.sap), v);
    if (v.name) byName.set(normName(v.name), v);
  }
  const rows: OrderSheetRow[] = [];
  let matched = 0;
  let orphans = 0;
  let skipped = 0;
  for (const p of parsed) {
    const hit = (p.sap && bySap.get(sapCore(p.sap))) || (p.name && byName.get(normName(p.name))) || null;
    if (hit) {
      rows.push({ sap: hit.sap, data: p.data });
      matched++;
      continue;
    }
    const key = orphanKey(p.sap, p.name);
    if (!key) { skipped++; continue; }
    rows.push({
      sap: key,
      data: p.data,
      ...(p.name ? { name: p.name } : {}),
      ...(p.model ? { model: p.model } : {}),
    });
    orphans++;
  }
  return { rows, matched, orphans, skipped };
}

/** a row of the order-sheet grid: a stable storage `key`, plus the display identity. */
export interface OrderSheetVessel {
  key: string;
  sap: string;
  name: string;
  model: string;
}

/** the order-sheet roster: ONLY the vessels actually on the order sheet — boats that have stored
 *  order-sheet data, whether imported from a SHEET 27 workbook or hand-entered. It is deliberately NOT
 *  auto-populated from the season overview: pulling in every overview boat (including ones nobody had put
 *  on the sheet) mixed uploaded and not-yet-uploaded boats and caused confusion. A SAP-keyed row borrows
 *  its name/model from the matching overview vessel (imported rows store only spec cells); departed
 *  ("Left") boats and hand-removed rows are dropped, and a stale name-key orphan is dropped when a SAP row
 *  for the same boat is already listed. */
export function orderSheetRoster(
  vessels: Array<{ sap: string; name: string; model: string; left?: boolean }>,
  sheet: Record<string, Record<string, string>>,
  hidden: Set<string>,
): OrderSheetVessel[] {
  // overview identity by SAP — names/models a matched (SAP-keyed) uploaded row and flags departed boats.
  const bySap = new Map<string, { sap: string; name: string; model: string; left?: boolean }>();
  for (const v of vessels) if (v.sap) bySap.set(v.sap, v);

  const rows: OrderSheetVessel[] = [];
  const seenNames = new Set<string>();
  // process real SAP rows before name-key orphans so a SAP row wins the name de-dupe for the same boat.
  const keys = Object.keys(sheet).sort(
    (a, b) => Number(a.startsWith('name:')) - Number(b.startsWith('name:')),
  );
  for (const key of keys) {
    if (hidden.has(key)) continue;
    const isNameKey = key.startsWith('name:');
    const ov = isNameKey ? undefined : bySap.get(key);
    if (ov?.left) continue; // departed boat - keep last season's vessels off the sheet
    const d = sheet[key] ?? {};
    const name = ov?.name ?? d[OS_NAME_KEY] ?? '';
    const model = ov?.model ?? d[OS_MODEL_KEY] ?? '';
    const nn = normName(name);
    if (nn && seenNames.has(nn)) continue; // the same boat is already listed under another key
    if (nn) seenNames.add(nn);
    rows.push({ key, sap: isNameKey ? '' : key, name, model });
  }
  return rows;
}
